import axios from "axios";
import throttle from "lodash-es/throttle";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { Auth, connectAuthEmulator } from "firebase/auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getFirestore, initializeFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { UserConfig } from "app/types";
import firebase, { fetchAuthToken } from "app/Auth";
import Constants from "common/constant";
import { client } from "common/apiClient";

export const connectEmulator = (auth: Auth) => {
  const host = Constants.firebaseEmulatorsHost;
  const authPort = Constants.firebaseEmulatorsAuthPort;
  const firestorePort = Number(Constants.firebaseEmulatorsFirestorePort);
  // dev:https（--mode https）ではエミュレータを https リバースプロキシ経由で叩く（kids と共有の TLS プロキシ）。
  // Firestore は connectFirestoreEmulator が ssl:false 固定のため initializeFirestore(ssl:true) を使う。
  if (import.meta.env.MODE === "https") {
    initializeFirestore(firebase, {
      host: `${host}:${firestorePort}`,
      ssl: true,
      experimentalForceLongPolling: true,
    });
    connectAuthEmulator(auth, `https://${host}:${authPort}`, { disableWarnings: true });
    return;
  }
  const db = getFirestore(firebase);
  connectFirestoreEmulator(db, host, firestorePort);
  connectAuthEmulator(auth, `http://${host}:${authPort}`, { disableWarnings: true });
};

export const logEventNative = async ({ event, params }: { event: string; params: object }) => {
  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      command: "logEvent",
      data: {
        event,
        params,
      },
    }),
  );
};

export const getParentConfig = createAsyncThunk(
  "app/getParentConfig",
  async ({ uid }: { uid: string }) => {
    const token = await fetchAuthToken();
    const response = await client({ token: token }).get(`/v1/user/config/${uid}`);
    return response.data.data;
  },
);

export const updateParentConfig = createAsyncThunk(
  "app/updateParentConfig",
  throttle(async ({ uid }: { uid: string }, thunkAPI) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { config } = thunkAPI.getState() as any;
    const token = await fetchAuthToken();
    await client({ token: token }).put(`/v1/user/config/${uid}`, config.parentConfig);
    return;
  }, 1000),
);

export const getNotifications = createAsyncThunk("app/getNotifications", async () => {
  const token = await fetchAuthToken();
  const response = await client({ token: token }).get("/v1/notifications");
  return response.data.data;
});

export const getAllNotifications = createAsyncThunk("app/getAllNotifications", async () => {
  const token = await fetchAuthToken();
  const response = await client({ token: token }).get("/v1/notifications/all");
  return response.data.data;
});

export const uploadImageFile = async ({
  is_sync,
  fileBlob,
  filename,
  metadata,
  contentType,
}: {
  is_sync?: boolean;
  fileBlob: Blob | File;
  filename: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: any;
  contentType?: string;
}) => {
  const token = await fetchAuthToken();
  const client = axios.create({ baseURL: Constants.apiHost });
  client.defaults.headers.common["Authorization"] = token;
  const formData = new FormData();
  formData.append("file", fileBlob);
  formData.append("filename", filename);
  formData.append("metadata", JSON.stringify(metadata));
  formData.append("contentType", contentType || "");
  return client.post(`/v1/upload/file/image?is_sync=${is_sync ?? true}`, formData);
};

export const uploadImageAsset = async ({
  uid,
  is_sync,
  fileBlob,
  object_key,
  filename,
  tags,
  metadata,
  contentType,
}: {
  uid: string;
  is_sync?: boolean;
  fileBlob: Blob | File;
  object_key: string;
  filename: string;
  tags: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: any;
  contentType?: string;
}) => {
  const token = await fetchAuthToken();
  const client = axios.create({ baseURL: Constants.apiHost });
  client.defaults.headers.common["Authorization"] = token;
  const formData = new FormData();
  formData.append("file", fileBlob);
  formData.append("filename", filename);
  formData.append("tags", tags);
  formData.append("object_key", object_key);
  formData.append("metadata", JSON.stringify(metadata));
  formData.append("contentType", contentType || "");
  return client.post(`/v1/asset/user/${uid}/image?is_sync=${is_sync ?? true}`, formData);
};

export const updateImageAsset = async ({
  id,
  name,
  tags,
}: {
  id: number;
  name: string;
  tags: string;
}) => {
  const token = await fetchAuthToken();
  return client({ token }).put(`/v1/asset/${id}`, { name, tags });
};

export const updateImageAssetMetadata = async ({
  id,
  metadata,
}: {
  id: number;
  metadata: Record<string, unknown>;
}) => {
  const token = await fetchAuthToken();
  return client({ token }).put(`/v1/asset/${id}`, { metadata });
};

export const deleteAsset = async ({ id }: { id: number }) => {
  const token = await fetchAuthToken();
  return client({ token }).delete(`/v1/asset/${id}`);
};

// Child player config API
export const fetchUserConfig = async ({ uid }: { uid: string }) => {
  const token = await fetchAuthToken();
  const response = await client({ token: token }).get(`/v1/user/config/${uid}`);
  return response.data.data.user_config;
};

export const useUserConfig = (uid: string) => {
  return useQuery({
    queryKey: ["user/config", uid],
    queryFn: () => fetchUserConfig({ uid }),
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useUpdateUserConfig = (uid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: UserConfig) => {
      const token = await fetchAuthToken();
      return client({ token: token }).put(`/v1/user/config/${uid}`, config);
    },
    onSuccess: (_, newConfig) => {
      queryClient.setQueryData(["user/config", uid], newConfig);
    },
  });
};
